/**
 * Whop Integration Control Center
 *
 * Platform-level Whop integration: API setup, product routing, connector inbox.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { useTheme } from '../../../../context/ThemeContext';
import {
  IntegrationLoadingSpinner,
  IntegrationSetupNotice,
  integrationT,
} from '../../../../components/platform/IntegrationFetchState';
import {
  ArrowLeft,
  ShoppingCart,
  Key,
  Route,
  Zap,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Shield,
  Eye,
  EyeOff,
  Save,
  Copy,
  ExternalLink,
  Inbox,
  Link2,
  BookOpen,
  Users,
  Package,
  Activity,
  Settings,
  ChevronDown,
  ChevronRight,
  Clock,
  CreditCard,
  Database,
  Ban,
  Webhook,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { toast } from 'sonner';
import { formatDateTimeWithTZ } from '../../../../utils/timezone';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '').replace(/\/api$/, '');

const PRODUCTION_WEBHOOK_URL = 'https://app.fundelite.ai/api/webhooks/whop';

const MAIN_TABS = [
  { id: 'api-setup', label: 'API Setup', icon: Key },
  { id: 'product-routing', label: 'Product & Link Routing', icon: Route },
  { id: 'connector', label: 'Connector', icon: Zap },
];

const ROUTING_SUBTABS = [
  { id: 'routing-mode', label: 'Routing Mode' },
  { id: 'mappings', label: 'Mappings' },
  { id: 'intake', label: 'Intake Queue' },
  { id: 'sync-logs', label: 'Sync & Logs' },
];

const CONNECTOR_SUBTABS = [
  { id: 'automations', label: 'Automations' },
  { id: 'triggers', label: 'Trigger Catalog' },
  { id: 'events', label: 'Event Inbox' },
  { id: 'resources', label: 'Resource Discovery' },
];

const STATUS_CONFIG = {
  connected: { bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
  active: { bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
  healthy: { bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
  missing_setup: { bg: 'bg-red-100 dark:bg-red-500/20', text: 'text-red-700 dark:text-red-400', icon: XCircle },
  not_configured: { bg: 'bg-stone-100 dark:bg-stone-500/20', text: 'text-stone-600 dark:text-stone-400', icon: XCircle },
  degraded: { bg: 'bg-amber-100 dark:bg-amber-500/20', text: 'text-amber-700 dark:text-amber-400', icon: AlertTriangle },
  partial: { bg: 'bg-amber-100 dark:bg-amber-500/20', text: 'text-amber-700 dark:text-amber-400', icon: AlertTriangle },
  error: { bg: 'bg-red-100 dark:bg-red-500/20', text: 'text-red-700 dark:text-red-400', icon: XCircle },
  unknown: { bg: 'bg-stone-100 dark:bg-stone-500/20', text: 'text-stone-600 dark:text-stone-400', icon: Clock },
};

const BANNER_LABELS = {
  connected: 'Connected',
  missing_setup: 'Setup Required',
  not_configured: 'Not Configured',
  degraded: 'Degraded',
  partial: 'Partial Setup',
  error: 'Error',
  unknown: 'Status Unknown',
};

const DEFAULT_UNAVAILABLE_CONFIG = {
  provider: 'whop',
  configured: false,
  enabled: false,
  status: 'not_configured',
  source: 'default',
  settings: {},
  api_key_configured: false,
  webhook_secret_configured: false,
  company_id_configured: false,
  updated_at: null,
  is_active: false,
};

function authHeaders(token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function apiFetch(path, token, options = {}) {
  const res = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    credentials: 'include',
    headers: { ...authHeaders(token), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.detail || data.message || `Request failed (${res.status})`);
    err.response = { status: res.status, data };
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

function loadMockData(setters) {
  setters.setConfig({
    configured: true,
    is_active: true,
    company_id: 'biz_mock',
    api_key_configured: true,
    webhook_secret_configured: true,
  });
  setters.setStatus({
    status: 'connected',
    status_reason: 'Development mock data',
    configuration: { has_api_key: true, has_webhook_secret: true, has_company_id: true },
    webhook_activity: { recent_webhooks_1h: 3, message: 'Mock activity' },
    activity: { active_memberships: 12 },
  });
  setters.setRouting({
    routing_mode: 'mapped_only',
    routing_modes: [{ value: 'mapped_only', label: 'Mapped only', description: 'Mock' }],
    unmapped_behaviors: [{ value: 'intake_only', label: 'Intake', description: 'Mock' }],
  });
  setters.setMappings([
    { id: 'wpm_mock1', fundelite_plan_name: 'Self Repair Basic', whop_plan_id: 'plan_mock', is_active: true },
  ]);
  setters.setIntake([
    { id: 'int_mock1', email: 'demo@example.com', created_at: new Date().toISOString(), status: 'needs_review', whop_product: { name: 'Mock Product' } },
  ]);
  setters.setSyncLogs([
    { id: 'sync_mock1', status: 'success', started_at: new Date().toISOString(), sync_type: 'plans' },
  ]);
  setters.setAutomations([
    { id: 'auto_mock1', name: 'Welcome on join', status: 'active', trigger_event: 'membership.went_valid' },
  ]);
  setters.setTriggers({ grouped: { membership: [{ id: 'new_member', name: 'New Member Joined', description: 'Mock' }] }, total: 1 });
  setters.setEvents([
    { id: 'evt_mock1', event_type: 'membership.went_valid', email: 'demo@example.com', status: 'processed', source: 'webhook', received_at: new Date().toISOString() },
  ]);
  setters.setResourceCounts({ product: 2, plan: 5, member: 10, membership: 8 });
}

const StatusBadge = ({ status, size = 'default' }) => {
  const config = STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.unknown;
  const Icon = config.icon;
  const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1';
  const label = status ? String(status).replace(/_/g, ' ') : 'Unknown';

  return (
    <Badge className={`${config.bg} ${config.text} border-0 ${sizeClasses} inline-flex items-center gap-1 capitalize`}>
      <Icon size={size === 'sm' ? 10 : 12} />
      {label}
    </Badge>
  );
};

const MetricCard = ({ title, value, icon: Icon, status, subtitle, isDark }) => (
  <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200'}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-medium truncate ${isDark ? 'text-zinc-400' : 'text-stone-500'}`}>{title}</p>
          {status ? (
            <div className="mt-1">
              <StatusBadge status={status} size="sm" />
            </div>
          ) : (
            <p className={`text-xl font-bold mt-1 ${isDark ? 'text-white' : 'text-stone-900'}`}>{value ?? '—'}</p>
          )}
          {subtitle && <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-stone-400'}`}>{subtitle}</p>}
        </div>
        {Icon && (
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ml-2 ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
            <Icon size={16} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const SectionHeader = ({ title, description, icon: Icon, action, isDark }) => (
  <div className="flex items-start justify-between mb-4">
    <div className="flex items-start gap-3">
      {Icon && (
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
          <Icon size={20} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
        </div>
      )}
      <div>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-stone-900'}`}>{title}</h3>
        {description && <p className={`text-sm mt-0.5 ${isDark ? 'text-zinc-400' : 'text-stone-500'}`}>{description}</p>}
      </div>
    </div>
    {action}
  </div>
);

const TabButton = ({ active, onClick, icon: Icon, label, isDark, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
      active
        ? `border-emerald-500 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`
        : `border-transparent ${isDark ? 'text-zinc-400 hover:text-zinc-200' : 'text-stone-500 hover:text-stone-700'}`
    }`}
  >
    {Icon && <Icon size={16} />}
    {label}
  </button>
);

const SubTabButton = ({ active, onClick, label, isDark }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
      active
        ? `${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`
        : `${isDark ? 'text-zinc-400 hover:bg-zinc-800' : 'text-stone-500 hover:bg-stone-100'}`
    }`}
  >
    {label}
  </button>
);

const EmptyState = ({ message, isDark }) => (
  <div className={`text-center py-12 rounded-xl border border-dashed ${isDark ? 'border-zinc-700 text-zinc-500' : 'border-stone-200 text-stone-500'}`}>
    <Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />
    <p className="text-sm">{message}</p>
  </div>
);

const EndpointUnavailableBanner = ({ isDark, onRetry, retrying }) => (
  <div
    className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border ${
      isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
    }`}
    data-testid="whop-endpoint-unavailable-banner"
  >
    <div className="flex items-start gap-3 flex-1 min-w-0">
      <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
      <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-stone-700'}`}>
        {integrationT('platformSettings.integrations.whop.messages.settingsEndpointUnavailable')}
      </p>
    </div>
    <Button
      variant="outline"
      size="sm"
      onClick={onRetry}
      disabled={retrying}
      className={isDark ? 'border-zinc-700 shrink-0' : 'shrink-0'}
      data-testid="whop-config-retry-btn"
    >
      <RefreshCw size={16} className={`mr-2 ${retrying ? 'animate-spin' : ''}`} />
      {integrationT('platformSettings.integrations.common.actions.retry')}
    </Button>
  </div>
);

const APISetupTab = ({
  isDark,
  config,
  status,
  form,
  setForm,
  showSecrets,
  setShowSecrets,
  saving,
  onSave,
  onToggleActive,
  onRefresh,
  refreshing,
  actionsDisabled,
}) => {
  const cardClass = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200';
  const muted = isDark ? 'text-zinc-400' : 'text-stone-500';
  const webhookUrl = status?.webhook_url || PRODUCTION_WEBHOOK_URL;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard title="API Key" status={config?.api_key_configured || status?.configuration?.has_api_key ? 'connected' : 'missing_setup'} isDark={isDark} icon={Key} />
        <MetricCard title="Webhook Secret" status={config?.webhook_secret_configured || status?.configuration?.has_webhook_secret ? 'connected' : 'missing_setup'} isDark={isDark} icon={Webhook} />
        <MetricCard title="Company ID" status={config?.company_id_configured || config?.company_id || status?.configuration?.has_company_id ? 'connected' : 'missing_setup'} isDark={isDark} icon={Package} />
        <MetricCard title="Active Memberships" value={status?.activity?.active_memberships ?? 0} isDark={isDark} icon={Users} />
      </div>

      <Card className={cardClass}>
        <CardHeader>
          <SectionHeader title="API Setup" description="Configure Whop API credentials and defaults" icon={Key} isDark={isDark} action={
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing || actionsDisabled} className={isDark ? 'border-zinc-700' : ''}>
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          } />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className={isDark ? 'text-zinc-300' : ''}>API Key {config?.api_key_configured && <span className="text-xs text-emerald-500">(configured)</span>}</Label>
              <div className="relative mt-1">
                <Input
                  type={showSecrets ? 'text' : 'password'}
                  placeholder={config?.api_key_configured ? '••••••••••••' : 'Enter Whop API key'}
                  value={form.api_key}
                  onChange={(e) => setForm((p) => ({ ...p, api_key: e.target.value }))}
                  className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}
                />
                <button type="button" className="absolute right-2 top-2.5 text-zinc-400" onClick={() => setShowSecrets((s) => !s)}>
                  {showSecrets ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-zinc-300' : ''}>Webhook Secret {config?.webhook_secret_configured && <span className="text-xs text-emerald-500">(configured)</span>}</Label>
              <Input
                type={showSecrets ? 'text' : 'password'}
                placeholder={config?.webhook_secret_configured ? '••••••••••••' : 'Enter webhook secret'}
                value={form.webhook_secret}
                onChange={(e) => setForm((p) => ({ ...p, webhook_secret: e.target.value }))}
                className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              />
            </div>
            <div>
              <Label className={isDark ? 'text-zinc-300' : ''}>Company ID</Label>
              <Input value={form.company_id} onChange={(e) => setForm((p) => ({ ...p, company_id: e.target.value }))} className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} />
            </div>
            <div>
              <Label className={isDark ? 'text-zinc-300' : ''}>Default Product ID</Label>
              <Input value={form.default_product_id} onChange={(e) => setForm((p) => ({ ...p, default_product_id: e.target.value }))} className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} />
            </div>
            <div>
              <Label className={isDark ? 'text-zinc-300' : ''}>Default Plan ID</Label>
              <Input value={form.default_plan_id} onChange={(e) => setForm((p) => ({ ...p, default_plan_id: e.target.value }))} className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} />
            </div>
          </div>

          <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-zinc-800/50' : 'bg-stone-50'}`}>
            <div>
              <Label className={isDark ? 'text-white' : ''}>Integration Active</Label>
              <p className={`text-xs ${muted}`}>Enable Whop webhook processing</p>
            </div>
            <Switch checked={!!config?.is_active} onCheckedChange={onToggleActive} disabled={actionsDisabled} />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={onSave} disabled={saving || actionsDisabled} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
              Save Configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader>
          <CardTitle className={isDark ? 'text-white' : ''}>Webhook URL</CardTitle>
          <CardDescription className={muted}>Register this URL in your Whop dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input readOnly value={webhookUrl} className={isDark ? 'bg-zinc-800 border-zinc-700 font-mono text-sm' : 'font-mono text-sm'} />
            <Button variant="outline" size="icon" onClick={() => copy(webhookUrl)} className={isDark ? 'border-zinc-700' : ''}>
              <Copy size={16} />
            </Button>
            <Button variant="outline" size="icon" asChild className={isDark ? 'border-zinc-700' : ''}>
              <a href="https://dash.whop.com" target="_blank" rel="noopener noreferrer">
                <ExternalLink size={16} />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const RoutingModeContent = ({ isDark, routing, saving, onSaveRouting, actionsDisabled }) => {
  const cardClass = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200';
  const [local, setLocal] = useState({
    routing_mode: routing?.routing_mode || 'mapped_only',
    unmapped_behavior: routing?.unmapped_behavior || 'intake_only',
    allow_free_products_access: routing?.allow_free_products_access || false,
    require_email_for_contact: routing?.require_email_for_contact !== false,
  });

  useEffect(() => {
    if (routing) {
      setLocal({
        routing_mode: routing.routing_mode || 'mapped_only',
        unmapped_behavior: routing.unmapped_behavior || 'intake_only',
        allow_free_products_access: !!routing.allow_free_products_access,
        require_email_for_contact: routing.require_email_for_contact !== false,
      });
    }
  }, [routing]);

  if (!routing) return <EmptyState message="No routing configuration loaded" isDark={isDark} />;

  return (
    <Card className={cardClass}>
      <CardHeader>
        <SectionHeader title="Routing Mode" description="Control how unmapped Whop purchases are handled" icon={Route} isDark={isDark} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className={isDark ? 'text-zinc-300' : ''}>Routing Mode</Label>
          <Select value={local.routing_mode} onValueChange={(v) => setLocal((p) => ({ ...p, routing_mode: v }))}>
            <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(routing.routing_modes || []).map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-stone-400'}`}>
            {(routing.routing_modes || []).find((m) => m.value === local.routing_mode)?.description}
          </p>
        </div>
        <div>
          <Label className={isDark ? 'text-zinc-300' : ''}>Unmapped Behavior</Label>
          <Select value={local.unmapped_behavior} onValueChange={(v) => setLocal((p) => ({ ...p, unmapped_behavior: v }))}>
            <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(routing.unmapped_behaviors || []).map((b) => (
                <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-zinc-800/50' : 'bg-stone-50'}`}>
          <Label className={isDark ? 'text-white' : ''}>Allow free product access</Label>
          <Switch checked={local.allow_free_products_access} onCheckedChange={(v) => setLocal((p) => ({ ...p, allow_free_products_access: v }))} />
        </div>
        <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-zinc-800/50' : 'bg-stone-50'}`}>
          <Label className={isDark ? 'text-white' : ''}>Require email for contact</Label>
          <Switch checked={local.require_email_for_contact} onCheckedChange={(v) => setLocal((p) => ({ ...p, require_email_for_contact: v }))} />
        </div>
        <Button disabled={saving || actionsDisabled} onClick={() => onSaveRouting(local)} className="bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
          Save Routing Mode
        </Button>
      </CardContent>
    </Card>
  );
};

const MappingsContent = ({ isDark, mappings, loading }) => {
  const cardClass = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200';

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!mappings?.length) {
    return <EmptyState message="No plan mappings yet. Sync plans from Whop or create mappings in the admin tools." isDark={isDark} />;
  }

  return (
    <Card className={cardClass}>
      <CardHeader>
        <SectionHeader title="Mappings" description="Whop plan to FundElite access mappings" icon={Link2} isDark={isDark} />
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className={isDark ? 'border-zinc-800' : ''}>
              <TableHead>Plan Name</TableHead>
              <TableHead>Whop Plan ID</TableHead>
              <TableHead>Access Level</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappings.map((m) => (
              <TableRow key={m.id} className={isDark ? 'border-zinc-800' : ''}>
                <TableCell className={isDark ? 'text-white' : ''}>{m.fundelite_plan_name || m.mapping_name || '—'}</TableCell>
                <TableCell className="font-mono text-xs">{m.whop_plan_id || '—'}</TableCell>
                <TableCell>{m.fundelite_access_level || m.access_level || '—'}</TableCell>
                <TableCell><StatusBadge status={m.is_active !== false ? 'active' : 'inactive'} size="sm" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const IntakeQueueContent = ({ isDark, items, loading, onReject, onApprove, actionsDisabled }) => {
  const cardClass = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200';

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!items?.length) {
    return <EmptyState message="Intake queue is empty — no unmapped purchases pending review." isDark={isDark} />;
  }

  return (
    <Card className={cardClass}>
      <CardHeader>
        <SectionHeader title="Intake Queue" description="Unmapped Whop purchases awaiting mapping" icon={Inbox} isDark={isDark} />
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className={isDark ? 'border-zinc-800' : ''}>
              <TableHead>Email</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} className={isDark ? 'border-zinc-800' : ''}>
                <TableCell className={isDark ? 'text-white' : ''}>{item.email || '—'}</TableCell>
                <TableCell>{item.whop_product?.name || item.whop_product_id || item.product_name || '—'}</TableCell>
                <TableCell><StatusBadge status={item.status} size="sm" /></TableCell>
                <TableCell className="text-xs">{item.created_at ? formatDateTimeWithTZ(item.created_at) : '—'}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => onApprove(item.id)} disabled={actionsDisabled} className={isDark ? 'border-zinc-700' : ''}>
                    <CheckCircle2 size={14} className="mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onReject(item.id)} disabled={actionsDisabled} className={isDark ? 'border-zinc-700 text-red-400' : 'text-red-600'}>
                    <Ban size={14} className="mr-1" /> Reject
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const SyncLogsContent = ({ isDark, logs, loading, syncing, onSyncPlans, actionsDisabled }) => {
  const cardClass = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200';

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={onSyncPlans} disabled={syncing || actionsDisabled} className="bg-emerald-600 hover:bg-emerald-700">
          {syncing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
          Sync Plans from Whop
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : !logs?.length ? (
        <EmptyState message="No sync logs yet. Run a plan sync to populate history." isDark={isDark} />
      ) : (
        <Card className={cardClass}>
          <CardHeader>
            <SectionHeader title="Sync & Logs" description="Plan and membership sync history" icon={Activity} isDark={isDark} />
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-zinc-800' : ''}>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className={isDark ? 'border-zinc-800' : ''}>
                    <TableCell className={isDark ? 'text-white' : ''}>{log.sync_type || log.type || 'sync'}</TableCell>
                    <TableCell><StatusBadge status={log.status} size="sm" /></TableCell>
                    <TableCell className="text-xs">{log.started_at ? formatDateTimeWithTZ(log.started_at) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AutomationsContent = ({ isDark, automations, loading }) => {
  const cardClass = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200';

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!automations?.length) {
    return <EmptyState message="No Whop automations configured yet." isDark={isDark} />;
  }

  return (
    <Card className={cardClass}>
      <CardHeader>
        <SectionHeader title="Automations" description="Whop-triggered workflows" icon={Settings} isDark={isDark} />
      </CardHeader>
      <CardContent className="space-y-2">
        {automations.map((a) => (
          <div key={a.id} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-zinc-800/50' : 'bg-stone-50'}`}>
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-stone-900'}`}>{a.name}</p>
              <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-stone-400'}`}>{a.trigger_event}</p>
            </div>
            <StatusBadge status={a.status} size="sm" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const TriggerCatalogContent = ({ isDark, triggers, loading }) => {
  const cardClass = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200';
  const [expanded, setExpanded] = useState(null);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const grouped = triggers?.grouped || {};
  const categories = Object.keys(grouped);

  if (!categories.length) {
    return <EmptyState message="Trigger catalog unavailable or empty." isDark={isDark} />;
  }

  return (
    <Card className={cardClass}>
      <CardHeader>
        <SectionHeader title="Trigger Catalog" description="Available Whop connector triggers" icon={BookOpen} isDark={isDark} />
      </CardHeader>
      <CardContent className="space-y-2">
        {categories.map((cat) => (
          <div key={cat}>
            <button
              type="button"
              className={`w-full flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-zinc-800/50 hover:bg-zinc-800' : 'bg-stone-50 hover:bg-stone-100'}`}
              onClick={() => setExpanded(expanded === cat ? null : cat)}
            >
              <span className={`font-medium capitalize ${isDark ? 'text-white' : ''}`}>{cat}</span>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{grouped[cat]?.length || 0}</Badge>
                {expanded === cat ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
            </button>
            {expanded === cat && (
              <div className="mt-2 ml-2 space-y-1">
                {(grouped[cat] || []).map((t) => (
                  <div key={t.id} className={`p-2 text-sm rounded ${isDark ? 'text-zinc-300 bg-zinc-900' : 'text-stone-600 bg-white border'}`}>
                    <span className="font-medium">{t.name}</span>
                    {t.description && <p className={`text-xs mt-0.5 ${isDark ? 'text-zinc-500' : 'text-stone-400'}`}>{t.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const EventInboxContent = ({ isDark, events, loading }) => {
  const cardClass = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200';

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!events?.length) {
    return <EmptyState message="Event inbox is empty." isDark={isDark} />;
  }

  return (
    <Card className={cardClass}>
      <CardHeader>
        <SectionHeader title="Event Inbox" description="Webhook, sync, and polling events" icon={Inbox} isDark={isDark} />
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className={isDark ? 'border-zinc-800' : ''}>
              <TableHead>Type</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Received</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((evt) => (
              <TableRow key={evt.id} className={isDark ? 'border-zinc-800' : ''}>
                <TableCell className="font-mono text-xs">{evt.event_type || '—'}</TableCell>
                <TableCell>{evt.email || '—'}</TableCell>
                <TableCell>{evt.source || '—'}</TableCell>
                <TableCell><StatusBadge status={evt.status} size="sm" /></TableCell>
                <TableCell className="text-xs">{evt.received_at ? formatDateTimeWithTZ(evt.received_at) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const ResourceDiscoveryContent = ({ isDark, counts, loading }) => {
  const cardClass = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200';
  const types = [
    { key: 'product', label: 'Products', icon: Package },
    { key: 'plan', label: 'Plans', icon: CreditCard },
    { key: 'checkout_link', label: 'Checkout Links', icon: Link2 },
    { key: 'member', label: 'Members', icon: Users },
    { key: 'membership', label: 'Memberships', icon: Users },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <Card className={cardClass}>
      <CardHeader>
        <SectionHeader title="Resource Discovery" description="Discovered Whop resources from API sync" icon={Database} isDark={isDark} />
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {types.map(({ key, label, icon: Icon }) => (
            <div key={key} className={`p-4 rounded-xl border ${isDark ? 'border-zinc-800 bg-zinc-800/30' : 'border-stone-200 bg-stone-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={18} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
                <span className={`text-sm font-medium ${isDark ? 'text-white' : ''}`}>{label}</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-stone-900'}`}>{counts?.[key] ?? 0}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const ProductRoutingTab = ({ isDark, activeSubtab, setActiveSubtab, children }) => (
  <div className="space-y-4">
    <div className="flex flex-wrap gap-2">
      {ROUTING_SUBTABS.map((t) => (
        <SubTabButton key={t.id} active={activeSubtab === t.id} onClick={() => setActiveSubtab(t.id)} label={t.label} isDark={isDark} />
      ))}
    </div>
    {children}
  </div>
);

const ConnectorTab = ({ isDark, activeSubtab, setActiveSubtab, children }) => (
  <div className="space-y-4">
    <div className="flex flex-wrap gap-2">
      {CONNECTOR_SUBTABS.map((t) => (
        <SubTabButton key={t.id} active={activeSubtab === t.id} onClick={() => setActiveSubtab(t.id)} label={t.label} isDark={isDark} />
      ))}
    </div>
    {children}
  </div>
);

export default function PlatformWhopSettings() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const { isDark } = useTheme();

  const [coreLoading, setCoreLoading] = useState(true);
  const [coreLoaded, setCoreLoaded] = useState(false);
  const [configEndpointUnavailable, setConfigEndpointUnavailable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const initialCoreFetchDoneRef = useRef(false);
  const coreFetchInFlightRef = useRef(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState('api-setup');
  const [activeRoutingSubtab, setActiveRoutingSubtab] = useState('routing-mode');
  const [activeConnectorSubtab, setActiveConnectorSubtab] = useState('automations');

  const [form, setForm] = useState({
    api_key: '',
    webhook_secret: '',
    company_id: '',
    default_product_id: '',
    default_plan_id: '',
  });

  const [routing, setRouting] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [intake, setIntake] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [triggers, setTriggers] = useState(null);
  const [events, setEvents] = useState([]);
  const [resourceCounts, setResourceCounts] = useState({});

  const isPlatformOwner = user?.role === 'platform_owner' || user?.role === 'super_admin';
  const cardBorder = isDark ? 'border-zinc-800' : 'border-stone-200';

  const applyConfigToForm = useCallback((cfg) => {
    if (!cfg) return;
    setForm((prev) => ({
      ...prev,
      company_id: cfg.company_id || '',
      default_product_id: cfg.default_product_id || '',
      default_plan_id: cfg.default_plan_id || '',
      api_key: '',
      webhook_secret: '',
    }));
  }, []);

  const clearTabData = useCallback(() => {
    setRouting(null);
    setMappings([]);
    setIntake([]);
    setSyncLogs([]);
    setAutomations([]);
    setTriggers(null);
    setEvents([]);
    setResourceCounts({});
  }, []);

  const fetchCore = useCallback(async ({ isRetry = false } = {}) => {
    if (coreFetchInFlightRef.current) return;
    coreFetchInFlightRef.current = true;
    setCoreLoading(!coreLoaded);
    setConfigEndpointUnavailable(false);
    try {
      const cfg = await apiFetch('/platform/settings/integrations/whop', token);
      setConfig(cfg);
      applyConfigToForm(cfg);
      setConfigEndpointUnavailable(false);

      try {
        const st = await apiFetch('/platform/whop/status', token);
        setStatus(st);
      } catch {
        setStatus(null);
      }

      setCoreLoaded(true);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        loadMockData({
          setConfig,
          setStatus,
          setRouting,
          setMappings,
          setIntake,
          setSyncLogs,
          setAutomations,
          setTriggers,
          setEvents,
          setResourceCounts,
        });
        setConfigEndpointUnavailable(false);
        setCoreLoaded(true);
      } else {
        setConfigEndpointUnavailable(true);
        setConfig(DEFAULT_UNAVAILABLE_CONFIG);
        setStatus(null);
        clearTabData();
        setCoreLoaded(true);
      }
    } finally {
      setCoreLoading(false);
      coreFetchInFlightRef.current = false;
    }
  }, [token, applyConfigToForm, clearTabData, coreLoaded]);

  const fetchTabData = useCallback(async () => {
    if (!coreLoaded || configEndpointUnavailable || activeMainTab === 'api-setup') {
      return;
    }

    setTabLoading(true);
    try {
      if (activeMainTab === 'product-routing') {
        if (activeRoutingSubtab === 'routing-mode') {
          const data = await apiFetch('/platform/whop/routing-config', token);
          setRouting(data);
        } else if (activeRoutingSubtab === 'mappings') {
          const data = await apiFetch('/platform/whop/plan-mappings', token);
          setMappings(data.items || []);
        } else if (activeRoutingSubtab === 'intake') {
          const data = await apiFetch('/platform/whop/intake?limit=50', token);
          setIntake(data.items || []);
        } else if (activeRoutingSubtab === 'sync-logs') {
          const data = await apiFetch('/platform/whop/sync-logs?limit=20', token);
          setSyncLogs(data.items || []);
        }
      } else if (activeMainTab === 'connector') {
        if (activeConnectorSubtab === 'automations') {
          const data = await apiFetch('/platform/whop/automations?limit=50', token);
          setAutomations(data.items || []);
        } else if (activeConnectorSubtab === 'triggers') {
          const data = await apiFetch('/platform/whop/connector/triggers', token);
          setTriggers(data);
        } else if (activeConnectorSubtab === 'events') {
          const data = await apiFetch('/platform/whop/connector/events?limit=50', token);
          setEvents(data.events || []);
        } else if (activeConnectorSubtab === 'resources') {
          const types = ['product', 'plan', 'checkout_link', 'member', 'membership'];
          const counts = {};
          await Promise.all(
            types.map(async (type) => {
              try {
                const data = await apiFetch(`/platform/whop/connector/resources?resource_type=${type}&limit=1`, token);
                counts[type] = data.total ?? 0;
              } catch {
                counts[type] = 0;
              }
            })
          );
          setResourceCounts(counts);
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        loadMockData({
          setConfig,
          setStatus,
          setRouting,
          setMappings,
          setIntake,
          setSyncLogs,
          setAutomations,
          setTriggers,
          setEvents,
          setResourceCounts,
        });
      } else {
        if (activeMainTab === 'product-routing') {
          if (activeRoutingSubtab === 'routing-mode') setRouting(null);
          if (activeRoutingSubtab === 'mappings') setMappings([]);
          if (activeRoutingSubtab === 'intake') setIntake([]);
          if (activeRoutingSubtab === 'sync-logs') setSyncLogs([]);
        } else if (activeMainTab === 'connector') {
          if (activeConnectorSubtab === 'automations') setAutomations([]);
          if (activeConnectorSubtab === 'triggers') setTriggers(null);
          if (activeConnectorSubtab === 'events') setEvents([]);
          if (activeConnectorSubtab === 'resources') setResourceCounts({});
        }
      }
    } finally {
      setTabLoading(false);
    }
  }, [token, activeMainTab, activeRoutingSubtab, activeConnectorSubtab, coreLoaded, configEndpointUnavailable]);

  useEffect(() => {
    if (authLoading || !isPlatformOwner || initialCoreFetchDoneRef.current) return;
    initialCoreFetchDoneRef.current = true;
    fetchCore();
  }, [authLoading, isPlatformOwner, fetchCore]);

  const handleRetryConfig = useCallback(async () => {
    setRefreshing(true);
    await fetchCore({ isRetry: true });
    setRefreshing(false);
  }, [fetchCore]);

  useEffect(() => {
    fetchTabData();
  }, [fetchTabData]);

  const handleRefresh = async () => {
    if (configEndpointUnavailable) {
      await handleRetryConfig();
      return;
    }
    setRefreshing(true);
    await fetchCore({ isRetry: true });
    setRefreshing(false);
  };

  const actionsDisabled = configEndpointUnavailable;

  const handleSaveConfig = async () => {
    if (actionsDisabled) return;
    const payload = {};
    if (form.api_key.trim()) payload.api_key = form.api_key.trim();
    if (form.webhook_secret.trim()) payload.webhook_secret = form.webhook_secret.trim();
    if (form.company_id.trim()) payload.company_id = form.company_id.trim();
    if (form.default_product_id.trim()) payload.default_product_id = form.default_product_id.trim();
    if (form.default_plan_id.trim()) payload.default_plan_id = form.default_plan_id.trim();

    if (!Object.keys(payload).length) {
      toast.error('Please fill in at least one field');
      return;
    }

    setSaving(true);
    try {
      await apiFetch('/platform/settings/integrations/whop', token, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      toast.success('Configuration saved');
      setForm((p) => ({ ...p, api_key: '', webhook_secret: '' }));
      await fetchCore();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (active) => {
    if (actionsDisabled) return;
    try {
      await apiFetch('/platform/settings/integrations/whop', token, {
        method: 'POST',
        body: JSON.stringify({ is_active: active }),
      });
      toast.success(`Integration ${active ? 'activated' : 'deactivated'}`);
      await fetchCore();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to update status');
    }
  };

  const handleSaveRouting = async (local) => {
    if (actionsDisabled) return;
    setSaving(true);
    try {
      await apiFetch('/platform/whop/routing-config', token, {
        method: 'POST',
        body: JSON.stringify(local),
      });
      toast.success('Routing configuration saved');
      await fetchTabData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to save routing');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncPlans = async () => {
    if (actionsDisabled) return;
    setSyncing(true);
    try {
      const data = await apiFetch('/platform/whop/sync-plans', token, { method: 'POST', body: '{}' });
      toast.success(`Synced ${data.synced ?? 0} plans from Whop`);
      await fetchTabData();
      await fetchCore();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to sync plans');
    } finally {
      setSyncing(false);
    }
  };

  const handleRejectIntake = async (id) => {
    try {
      await apiFetch(`/platform/whop/intake/${id}/reject`, token, { method: 'POST', body: '{}' });
      toast.success('Intake item rejected');
      await fetchTabData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to reject intake item');
    }
  };

  const handleApproveIntake = () => {
    if (actionsDisabled) return;
    toast.info('Approve requires selecting a plan mapping. Use reprocess with mapping_id from the intake detail flow.');
  };

  if (authLoading || (coreLoading && !coreLoaded)) {
    return <IntegrationLoadingSpinner />;
  }

  if (!isPlatformOwner) {
    return (
      <div className="space-y-6 max-w-7xl mx-auto">
        <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-stone-200'}>
          <CardContent className="p-8 text-center">
            <Shield size={48} className={`mx-auto mb-4 ${isDark ? 'text-zinc-500' : 'text-stone-400'}`} />
            <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-stone-900'}`}>Access Restricted</h2>
            <p className={isDark ? 'text-zinc-400' : 'text-stone-500'}>Only platform owners can access Whop integration settings.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/platform/settings')}>
              <ArrowLeft size={16} className="mr-2" />
              Back to Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overallStatus = configEndpointUnavailable
    ? 'not_configured'
    : status?.status || (config?.configured ? 'connected' : 'not_configured');
  const bannerLabel = BANNER_LABELS[overallStatus] || BANNER_LABELS.unknown;
  const showSetupNotice =
    !configEndpointUnavailable && !config?.configured && overallStatus === 'not_configured';

  const renderMainContent = () => {
    if (activeMainTab === 'api-setup') {
      return (
        <APISetupTab
          isDark={isDark}
          config={config}
          status={status}
          form={form}
          setForm={setForm}
          showSecrets={showSecrets}
          setShowSecrets={setShowSecrets}
          saving={saving}
          onSave={handleSaveConfig}
          onToggleActive={handleToggleActive}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          actionsDisabled={actionsDisabled}
        />
      );
    }

    if (activeMainTab === 'product-routing') {
      return (
        <ProductRoutingTab isDark={isDark} activeSubtab={activeRoutingSubtab} setActiveSubtab={setActiveRoutingSubtab}>
          {activeRoutingSubtab === 'routing-mode' && (
            <RoutingModeContent
              isDark={isDark}
              routing={routing}
              saving={saving}
              onSaveRouting={handleSaveRouting}
              actionsDisabled={actionsDisabled}
            />
          )}
          {activeRoutingSubtab === 'mappings' && <MappingsContent isDark={isDark} mappings={mappings} loading={tabLoading} />}
          {activeRoutingSubtab === 'intake' && (
            <IntakeQueueContent
              isDark={isDark}
              items={intake}
              loading={tabLoading}
              onReject={handleRejectIntake}
              onApprove={handleApproveIntake}
              actionsDisabled={actionsDisabled}
            />
          )}
          {activeRoutingSubtab === 'sync-logs' && (
            <SyncLogsContent
              isDark={isDark}
              logs={syncLogs}
              loading={tabLoading}
              syncing={syncing}
              onSyncPlans={handleSyncPlans}
              actionsDisabled={actionsDisabled}
            />
          )}
        </ProductRoutingTab>
      );
    }

    if (activeMainTab === 'connector') {
      return (
        <ConnectorTab isDark={isDark} activeSubtab={activeConnectorSubtab} setActiveSubtab={setActiveConnectorSubtab}>
          {activeConnectorSubtab === 'automations' && <AutomationsContent isDark={isDark} automations={automations} loading={tabLoading} />}
          {activeConnectorSubtab === 'triggers' && <TriggerCatalogContent isDark={isDark} triggers={triggers} loading={tabLoading} />}
          {activeConnectorSubtab === 'events' && <EventInboxContent isDark={isDark} events={events} loading={tabLoading} />}
          {activeConnectorSubtab === 'resources' && <ResourceDiscoveryContent isDark={isDark} counts={resourceCounts} loading={tabLoading} />}
        </ConnectorTab>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="platform-whop-settings">
      {configEndpointUnavailable && (
        <EndpointUnavailableBanner isDark={isDark} onRetry={handleRetryConfig} retrying={refreshing} />
      )}
      {showSetupNotice && <IntegrationSetupNotice providerName="Whop" />}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/platform/settings')} className={isDark ? 'text-zinc-400 hover:text-white' : ''}>
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ShoppingCart size={24} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
              <h1 className={`text-xl md:text-2xl font-bold ${isDark ? 'text-white' : 'text-stone-900'}`}>Whop Integration</h1>
            </div>
            <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-stone-500'}`}>
              Manage payment plans, routing, intake, and connector events
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={overallStatus} />
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className={isDark ? 'border-zinc-700' : ''}>
            <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {status?.status_reason && !configEndpointUnavailable && (
        <div
          className={`flex items-start gap-3 p-4 rounded-xl border ${
            overallStatus === 'connected'
              ? isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'
              : overallStatus === 'degraded' || overallStatus === 'partial'
                ? isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
                : isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
          }`}
        >
          {overallStatus === 'connected' ? (
            <CheckCircle2 className={`w-5 h-5 mt-0.5 shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
          ) : overallStatus === 'degraded' || overallStatus === 'partial' ? (
            <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
          ) : (
            <XCircle className={`w-5 h-5 mt-0.5 shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
          )}
          <div className="flex-1 min-w-0">
            <p className={`font-semibold ${isDark ? 'text-white' : 'text-stone-900'}`}>{bannerLabel}</p>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-stone-600'}`}>{status.status_reason}</p>
            {status.webhook_activity && (
              <p className={`text-xs mt-2 ${isDark ? 'text-zinc-500' : 'text-stone-500'}`}>
                {status.webhook_activity.message || `Webhooks (1h): ${status.webhook_activity.recent_webhooks_1h ?? 0}`}
              </p>
            )}
          </div>
        </div>
      )}

      <div className={`border-b ${cardBorder}`}>
        <div className="flex overflow-x-auto no-scrollbar -mb-px">
          {MAIN_TABS.map((tab) => (
            <TabButton
              key={tab.id}
              active={activeMainTab === tab.id}
              onClick={() => setActiveMainTab(tab.id)}
              icon={tab.icon}
              label={tab.label}
              isDark={isDark}
              testId={`whop-tab-${tab.id}`}
            />
          ))}
        </div>
      </div>

      {renderMainContent()}
    </div>
  );
}
